import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * SchemaGenerator - Unified Schema.org Markup Generator for 2026 AIO SEO
 * Generates structured data optimized for Google AI Overviews
 * 
 * Supports:
 * - FAQPage Schema (mandatory for 2026)
 * - HowTo Schema (for setup guides)
 * - Article Schema (with dateModified freshness)
 * - Product Schema (for subscription pages)
 * - BreadcrumbList Schema (navigation)
 * - WebSite Schema with SearchAction
 */

// Base website info
const SITE_INFO = {
  name: 'XtreamGate',
  url: 'https://xtreamgate.com',
  logo: 'https://xtreamgate.com/logo.png',
  description: 'Official XtreamCube Gateway - Best IPTV service provider 2026. Premium IPTV subscription with 20,000+ channels.',
};

/**
 * Generate FAQ Schema for Google AI Overviews
 * Critical for 2026 AIO ranking
 */
export const generateFAQSchema = (faqItems) => {
  if (!faqItems || faqItems.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqItems.map(item => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer
      }
    }))
  };
};

/**
 * Generate How-To Schema for setup guides
 * Essential for device setup and troubleshooting articles
 */
export const generateHowToSchema = (howTo) => {
  if (!howTo || !howTo.steps || howTo.steps.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': howTo.name,
    'description': howTo.description,
    'totalTime': howTo.totalTime || 'PT10M',
    'estimatedCost': howTo.cost || {
      '@type': 'MonetaryAmount',
      'currency': 'USD',
      'value': '0'
    },
    'supply': howTo.supplies || [],
    'tool': howTo.tools || [],
    'step': howTo.steps.map((step, index) => ({
      '@type': 'HowToStep',
      'position': index + 1,
      'name': step.name,
      'text': step.text,
      'url': step.url || `${SITE_INFO.url}#step-${index + 1}`,
      ...(step.image && { 'image': step.image })
    }))
  };
};

/**
 * Generate Article Schema with freshness signals for 2026
 */
export const generateArticleSchema = (article) => {
  if (!article) return null;

  const datePublished = article.datePublished || article.date || '2026-01-01';
  const dateModified = article.dateModified || article.lastUpdated || '2026-01-15';

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.excerpt || article.description,
    'image': article.image || `${SITE_INFO.url}/og-image.png`,
    'author': {
      '@type': 'Organization',
      'name': 'XtreamGate Editorial Team',
      'url': SITE_INFO.url
    },
    'publisher': {
      '@type': 'Organization',
      'name': SITE_INFO.name,
      'logo': {
        '@type': 'ImageObject',
        'url': SITE_INFO.logo
      }
    },
    'datePublished': datePublished,
    'dateModified': dateModified,
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': article.url || `${SITE_INFO.url}/blog/${article.slug}`
    },
    'keywords': article.keywords || 'iptv, best iptv service, iptv subscription, streaming',
    'articleSection': article.category || 'IPTV Guides',
    'wordCount': article.wordCount || 1500,
    'inLanguage': 'en-US',
    // Speakable schema for voice search optimization
    ...(article.quickAnswer && {
      'speakable': {
        '@type': 'SpeakableSpecification',
        'cssSelector': ['.quick-answer', '.ai-summary', '.expanded-answer']
      }
    })
  };
};

/**
 * Generate Speakable Schema for voice search
 * Critical for voice assistant optimization
 */
export const generateSpeakableSchema = (article) => {
  if (!article || !article.quickAnswer) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': article.title,
    'speakable': {
      '@type': 'SpeakableSpecification',
      'cssSelector': ['.quick-answer', '.ai-summary', '.key-points']
    },
    'url': article.url || `https://xtreamgate.com/blog/${article.slug}`
  };
};

/**
 * Generate SportsEvent Schema for live scores
 * Rich results for sports queries
 */
export const generateSportsEventSchema = (event) => {
  if (!event) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    'name': event.name || `${event.homeTeam} vs ${event.awayTeam}`,
    'startDate': event.startDate,
    'endDate': event.endDate,
    'location': {
      '@type': 'Place',
      'name': event.venue || 'Stadium',
      'address': event.location
    },
    'homeTeam': {
      '@type': 'SportsTeam',
      'name': event.homeTeam
    },
    'awayTeam': {
      '@type': 'SportsTeam',
      'name': event.awayTeam
    },
    'competitor': [
      { '@type': 'SportsTeam', 'name': event.homeTeam },
      { '@type': 'SportsTeam', 'name': event.awayTeam }
    ],
    ...(event.sport && { 'sport': event.sport }),
    ...(event.eventStatus && { 'eventStatus': `https://schema.org/${event.eventStatus}` })
  };
};

/**
 * Generate Product Schema for subscription/service pages
 */
export const generateProductSchema = (product) => {
  if (!product) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': product.name || 'XtreamGate IPTV Subscription',
    'description': product.description || 'Premium IPTV subscription with 20,000+ channels, 4K streaming, and 24/7 support.',
    'brand': {
      '@type': 'Brand',
      'name': 'XtreamCube'
    },
    'offers': {
      '@type': 'AggregateOffer',
      'priceCurrency': 'USD',
      'lowPrice': product.lowPrice || '9.99',
      'highPrice': product.highPrice || '49.99',
      'offerCount': product.offerCount || '4',
      'availability': 'https://schema.org/InStock'
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': product.rating || '4.9',
      'reviewCount': product.reviewCount || '10000',
      'bestRating': '5',
      'worstRating': '1'
    },
    'review': product.reviews || [{
      '@type': 'Review',
      'reviewRating': {
        '@type': 'Rating',
        'ratingValue': '5',
        'bestRating': '5'
      },
      'author': {
        '@type': 'Person',
        'name': 'Verified Customer'
      },
      'reviewBody': 'Best IPTV service I have ever used. Crystal clear 4K streaming and excellent customer support.'
    }]
  };
};

/**
 * Generate BreadcrumbList Schema for navigation
 * Helps Google understand site structure
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': crumb.name,
      'item': crumb.url || `${SITE_INFO.url}${crumb.path}`
    }))
  };
};

/**
 * Generate WebSite Schema with SearchAction
 * Critical for Google sitelinks search box
 */
export const generateWebSiteSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': SITE_INFO.name,
    'url': SITE_INFO.url,
    'description': SITE_INFO.description,
    'publisher': {
      '@type': 'Organization',
      'name': SITE_INFO.name,
      'logo': SITE_INFO.logo
    },
    'potentialAction': {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': `${SITE_INFO.url}/blog?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    },
    'inLanguage': 'en-US'
  };
};

/**
 * Generate Organization Schema with enhanced 2026 signals
 */
export const generateOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'XtreamGate Official',
    'alternateName': 'XtreamCube Gateway',
    'url': SITE_INFO.url,
    'logo': SITE_INFO.logo,
    'description': 'Best IPTV service provider 2026. Premium IPTV subscription with 20,000+ channels, 25 global servers, and 24/7 support. Trusted by 27.5K+ clients since 2017.',
    'foundingDate': '2017',
    'sameAs': [
      'https://xtreamcube.com',
      'https://xtreamcube.com/freetrialiptv/'
    ],
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'Customer Support',
      'availableLanguage': ['English'],
      'areaServed': ['US', 'CA', 'GB', 'AU', 'Worldwide']
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.9',
      'reviewCount': '10000',
      'bestRating': '5',
      'worstRating': '1'
    }
  };
};

/**
 * Generate VideoObject Schema for embedded videos
 * Placeholder for future video content
 */
export const generateVideoSchema = (video) => {
  if (!video) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    'name': video.name,
    'description': video.description,
    'thumbnailUrl': video.thumbnail,
    'uploadDate': video.uploadDate || '2026-01-15',
    'duration': video.duration || 'PT5M',
    'contentUrl': video.url,
    'embedUrl': video.embedUrl,
    'publisher': {
      '@type': 'Organization',
      'name': SITE_INFO.name,
      'logo': {
        '@type': 'ImageObject',
        'url': SITE_INFO.logo
      }
    }
  };
};

/**
 * SchemaGenerator Component
 * Renders all applicable schemas based on props
 * Uses connected @graph structure for better schema interconnectivity
 */
export const SchemaGenerator = ({
  faqItems,
  howTo,
  article,
  product,
  breadcrumbs,
  // Default to false as these are now handled globally by SiteSchema.js (App.js)
  includeWebSite = false,
  includeOrganization = false,
  video
}) => {
  // Generate applicable schemas
  const faqSchema = generateFAQSchema(faqItems);
  const howToSchema = generateHowToSchema(howTo);
  const articleSchema = generateArticleSchema(article);
  const productSchema = generateProductSchema(product);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
  const videoSchema = generateVideoSchema(video);
  const webSiteSchema = includeWebSite ? generateWebSiteSchema() : null;
  const organizationSchema = includeOrganization ? generateOrganizationSchema() : null;

  // Build connected graph with all schemas
  const connectedGraphSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      organizationSchema || { '@id': SITE_INFO.url + '/#organization' },
      ...(webSiteSchema ? [webSiteSchema] : []),
      ...(breadcrumbSchema ? [breadcrumbSchema] : []),
      ...(articleSchema ? [{
        ...articleSchema,
        'publisher': {
          '@id': SITE_INFO.url + '/#organization'
        },
        'author': {
          '@id': SITE_INFO.url + '/#organization'
        }
      }] : []),
      ...(faqSchema ? [faqSchema] : []),
      ...(howToSchema ? [howToSchema] : []),
      ...(productSchema ? [productSchema] : []),
      ...(videoSchema ? [videoSchema] : [])
    ].filter(Boolean)
  };

  if (connectedGraphSchema['@graph'].length === 0) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(connectedGraphSchema)}
      </script>
    </Helmet>
  );
};

/**
 * Helper: Generate combined schema for blog articles
 * Includes Article + FAQ + Breadcrumb schemas
 */
export const generateBlogArticleSchemas = (article, faqItems, breadcrumbs) => {
  return {
    article: generateArticleSchema(article),
    faq: generateFAQSchema(faqItems),
    breadcrumb: generateBreadcrumbSchema(breadcrumbs)
  };
};

/**
 * Helper: Generate combined schema for device/regional pages
 * Includes FAQ + HowTo + Breadcrumb schemas
 */
export const generateLandingPageSchemas = (pageData) => {
  return {
    faq: generateFAQSchema(pageData.faqItems),
    howTo: generateHowToSchema(pageData.howTo),
    breadcrumb: generateBreadcrumbSchema(pageData.breadcrumbs),
    product: generateProductSchema(pageData.product)
  };
};

export default SchemaGenerator;
